import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Printer, Share2, Facebook, Twitter } from 'lucide-react';
import { motion } from 'framer-motion';
import { getEventById, getAnnouncementById, getBlogById } from '../services/api';
import ImageGallery from '../components/ImageGallery';


const NewsDetailPage = ({ type = 'events' }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);

    const isAnnouncement = type === 'announcements';
    const isBlog = type === 'blogs';

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isAnnouncement) result = await getAnnouncementById(id);
                else if (isBlog) result = await getBlogById(id);
                else result = await getEventById(id);

                setItem(result || null);
            } catch (error) {
                console.error("Detay çekilemedi:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, isAnnouncement, isBlog]);

    const handlePrint = () => {
        window.print();
    };

    const handleShare = (platform) => {
        const url = window.location.href;
        const text = item?.title || 'TURET Haber';

        if (platform === 'facebook') {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        } else if (platform === 'twitter') {
            window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-xl font-bold text-cyan-600">Yükleniyor...</div>
            </div>
        );
    }

    if (!item) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">İçerik Bulunamadı</h2>
                <div className="flex gap-4">
                    <button onClick={() => navigate(-1)} className="text-cyan-600 hover:underline">Geri Dön</button>
                    <button onClick={() => navigate('/')} className="text-cyan-600 hover:underline">Anasayfa</button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white text-gray-900 min-h-screen">
            <article className="pt-24 pb-20 px-0 md:px-6">
                <div className="container mx-auto max-w-4xl">
                    {/* Back Link */}
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-cyan-600 transition-colors mb-6 ml-4 md:ml-0 group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        Geri Dön
                    </button>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="bg-white md:rounded-[2rem] p-0 md:p-12 md:shadow-xl md:border border-gray-100 overflow-hidden"
                    >
                        {/* Header with Title and Social Icons */}
                        <div className="flex items-start justify-between gap-6 mb-6">
                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-gray-900 flex-1">
                                {item.title}
                            </h1>

                            {/* Social Share Icons */}
                            <div className="flex gap-2 flex-shrink-0">
                                <button
                                    onClick={handlePrint}
                                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-all"
                                    aria-label="Print"
                                >
                                    <Printer className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Meta Information */}
                        <div className="flex items-center gap-4 text-gray-600 text-sm mb-8 pb-6 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-cyan-600" />
                                <span>{item.date}</span>
                            </div>
                            {isBlog && item.author && (
                                <>
                                    <span className="w-1 h-1 bg-gray-400 rounded-full" />
                                    <div className="flex flex-col">
                                        <span className="font-bold text-gray-800">{item.author}</span>
                                        {item.authorRole && <span className="text-xs text-gray-500 italic">{item.authorRole}</span>}
                                    </div>
                                </>
                            )}
                            <span className="w-1 h-1 bg-gray-400 rounded-full" />
                            <span>MSKÜ TURET {isAnnouncement ? 'Duyuru' : (isBlog ? 'Köşe Yazısı' : 'Haber')}</span>
                        </div>

                        {/* Visuals */}
                        {/* A4 Poster for Announcements */}
                        {isAnnouncement && item.detailImage && (
                            <div className="mb-12 flex justify-center">
                                <div className="rounded-xl overflow-hidden shadow-2xl max-w-2xl w-full border border-gray-100">
                                    <img
                                        src={item.detailImage}
                                        alt={item.title}
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                            </div>
                        )}

                        {item.image && (
                            isBlog ? (
                                /* Blog Layout: Image + Author Info Floating Left */
                                <div className="mb-8 md:float-left md:w-80 md:mr-10">
                                    <div className="rounded-2xl overflow-hidden shadow-lg mb-4 bg-white border border-gray-100">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-auto object-cover"
                                        />
                                        <div className="p-4 text-center bg-gray-50/50">
                                            <div className="font-bold text-gray-900 text-lg mb-1">{item.author}</div>
                                            {item.authorRole && (
                                                <div className="text-sm text-cyan-600 font-medium italic">
                                                    {item.authorRole}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Standard Layout: Full Width Image for Events (Hidden for Announcements if no detailImage, but logic implies we hide Cover Image for Announcements regardless) */
                                !isAnnouncement && (
                                    <div className="mb-10 rounded-2xl overflow-hidden shadow-lg border border-gray-100 bg-gray-50">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            // Mobile: w-full h-auto (Natural height, no crop). 
                                            // Desktop (md+): max-h-[600px] object-cover (To prevent it from taking entire screen if huge)
                                            className={`w-full h-auto md:max-h-[600px] md:object-cover ${item.imagePosition || 'object-center'}`}
                                            // Force natural scaling on mobile to be 100% sure
                                            style={{ display: 'block' }}
                                        />
                                    </div>
                                )
                            )
                        )}



                        {/* Content */}
                        <div className="prose prose-lg max-w-none mb-12 prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-p:text-base md:prose-p:text-lg prose-a:text-cyan-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900">
                            {item.content && (() => {
                                // 1. Check for block-level HTML. If present, render as is.
                                if (/^\s*<(p|div|section|article|h[1-6]|ul|ol|blockquote)/i.test(item.content)) {
                                    return <div dangerouslySetInnerHTML={{ __html: item.content }} />;
                                }

                                // 2. Plain Text Parsing
                                let processedContent = item.content;

                                // Support Markdown Link: [Text](url)
                                // Allows multi-word text like [Bize Ulaşın](https://...)
                                const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
                                processedContent = processedContent.replace(markdownLinkPattern, (match, text, url) => {
                                    return `<a href="${url}" target="_blank" class="text-cyan-600 hover:underline font-bold">${text}</a>`;
                                });

                                // Legacy Pattern: text(url) or text (url)
                                // Only works for single words or small phrases to avoid matching too much
                                const legacyLinkPattern = /([^\[\s]+)\s*\((https?:\/\/[^)]+)\)/g;
                                processedContent = processedContent.replace(legacyLinkPattern, (match, text, url) => {
                                    // Avoid double replacing if it was already caught by markdown
                                    if (text.startsWith('[')) return match;
                                    return `<a href="${url}" target="_blank" class="text-cyan-600 hover:underline font-bold">${text}</a>`;
                                });

                                // Fallback: Raw URLs that weren't caught (e.g. "Tıklayınız: https://...")
                                // This ensures clickable links even if the user didn't format them as [text](url)
                                // We use a negative lookbehind to ensure we aren't inside an href="" or src="" attribute
                                // Simplified approach to avoid complex lookbehinds that might fail or be slow
                                const rawUrlPattern = /(?<!href="|src="|">)(https?:\/\/[^\s<]+)/g;
                                processedContent = processedContent.replace(rawUrlPattern, (match, url) => {
                                    return `<a href="${url}" target="_blank" class="text-cyan-600 hover:underline font-bold">Bağlantıya Git</a>`;
                                });

                                // Convert newlines to <br />
                                processedContent = processedContent.replace(/\n/g, '<br />');

                                return (
                                    <div
                                        dangerouslySetInnerHTML={{ __html: processedContent }}
                                        className="font-sans text-gray-700 leading-relaxed"
                                    />
                                );
                            })()}
                        </div>

                        {/* Clear float to ensure subsequent elements like gallery don't overlap */}
                        <div className="clear-both"></div>

                        {/* Image Gallery */}
                        {item.gallery && item.gallery.length > 0 && (
                            <div className="mt-12">
                                <ImageGallery images={item.gallery} />
                            </div>
                        )}
                    </motion.div>
                </div>
            </article>
        </div>
    );
};

export default NewsDetailPage;
